/**
 * ens-quote-based.js
 *
 * Etch 'N' Shine — starting-price acknowledgement gate for the quote-based
 * product template. Loaded ONLY by sections/product-template-1.liquid when
 * template.suffix is 'quote-based'; it is inert on any page without a
 * [data-ens-quote-ack] block, so it cannot affect the standard catalogue.
 *
 * The checkbox already carries `required`, which blocks a plain (no-JS) POST via
 * native constraint validation. This file upgrades that to an accessible inline
 * error and extends it to the submit paths native validation does not cover:
 *
 *   1. the theme's AJAX product form (theme.js AjaxCart, jQuery `submit`);
 *   2. Globo Product Options, which intercepts the purchase button's click and
 *      posts /cart/add itself;
 *   3. the sticky cart bar, which forwards a click to #AddToCart-<section>.
 *
 * All three are caught by listening on `document` in the CAPTURE phase, which
 * runs before any bubble-phase handler bound to the form or the button, and
 * stopping propagation there. Nothing is submitted, so the selected variant and
 * every personalisation field keep their values when validation fails.
 *
 * No libraries, no inline handlers, no globals.
 */

(function () {
  'use strict';

  var ACK_BLOCK = '[data-ens-quote-ack]';
  var ACK_INPUT = '[data-ens-quote-ack-input]';
  var ACK_ERROR = '[data-ens-quote-ack-error]';
  var INVALID_CLASS = 'ens-quote-ack--invalid';

  function closestForm(el) {
    while (el && el.nodeType === 1) {
      if (el.tagName === 'FORM') return el;
      el = el.parentNode;
    }
    return null;
  }

  function ackBlockIn(form) {
    return form ? form.querySelector(ACK_BLOCK) : null;
  }

  function ackBlockFor(el) {
    while (el && el.nodeType === 1) {
      if (el.hasAttribute && el.hasAttribute('data-ens-quote-ack')) return el;
      el = el.parentNode;
    }
    return null;
  }

  function markInvalid(block, moveFocus) {
    var input = block.querySelector(ACK_INPUT);
    var error = block.querySelector(ACK_ERROR);

    block.classList.add(INVALID_CLASS);
    if (error) error.hidden = false;
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      if (moveFocus !== false) {
        try {
          input.focus();
        } catch (e) {
          /* focus is a nicety; never let it break the gate */
        }
      }
    }
  }

  function markValid(block) {
    var input = block.querySelector(ACK_INPUT);
    var error = block.querySelector(ACK_ERROR);

    block.classList.remove(INVALID_CLASS);
    if (error) error.hidden = true;
    if (input) input.removeAttribute('aria-invalid');
  }

  /* True when this form carries an unchecked acknowledgement, i.e. the submit
     must be blocked. Also refreshes the visible state either way. */
  function mustBlock(form, moveFocus) {
    var block = ackBlockIn(form);
    if (!block) return false;

    var input = block.querySelector(ACK_INPUT);
    if (!input || input.checked) {
      markValid(block);
      return false;
    }

    markInvalid(block, moveFocus);
    return true;
  }

  function halt(evt) {
    evt.preventDefault();
    if (typeof evt.stopImmediatePropagation === 'function') evt.stopImmediatePropagation();
    evt.stopPropagation();
  }

  /* Nearest ancestor that would submit the form, or null. */
  function submitTrigger(target) {
    var el = target;
    while (el && el.nodeType === 1) {
      if (el.tagName === 'BUTTON') {
        var buttonType = (el.getAttribute('type') || 'submit').toLowerCase();
        return buttonType === 'submit' ? el : null;
      }
      if (el.tagName === 'INPUT') {
        var inputType = (el.getAttribute('type') || '').toLowerCase();
        return inputType === 'submit' || inputType === 'image' ? el : null;
      }
      el = el.parentNode;
    }
    return null;
  }

  /* 1. Native and jQuery form submits. */
  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!form || form.tagName !== 'FORM') return;
      if (mustBlock(form)) halt(evt);
    },
    true
  );

  /* 2. Purchase-button clicks — covers app handlers that never let a `submit`
        event fire, and the sticky bar's forwarded click. */
  document.addEventListener(
    'click',
    function (evt) {
      var trigger = submitTrigger(evt.target);
      if (!trigger) return;

      var form = trigger.form || closestForm(trigger);
      if (!form || !ackBlockIn(form)) return;
      if (mustBlock(form)) halt(evt);
    },
    true
  );

  /* 3. Native constraint validation (e.g. Enter in a text field). Suppress the
        browser bubble and show the same inline message instead. `invalid` does
        not bubble, so this listener must capture. */
  document.addEventListener(
    'invalid',
    function (evt) {
      var block = ackBlockFor(evt.target);
      if (!block) return;
      evt.preventDefault();
      markInvalid(block);
    },
    true
  );

  /* Clear the error as soon as the customer accepts. */
  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-ens-quote-ack-input')) return;

    var block = ackBlockFor(target);
    if (!block) return;
    if (target.checked) markValid(block);
  });

  /* ── Variant-aware price ─────────────────────────────────────────────────
     The acknowledgement names a figure, so on a product whose price varies by
     variant that figure has to follow the selection — otherwise a £21.99 twelve
     inch topper is acknowledged at £13.99.

     Only products with more than one variant opt in: the Liquid omits
     `data-ens-quote-ack-price-source` when `product.has_only_default_variant`,
     so the business sign never enters this path and its label can never be
     rewritten.

     Implemented as a MutationObserver on the theme's own price node rather than
     a `variantChange` listener, because that event is raised through jQuery's
     `.trigger()` and never dispatches a native DOM event — `addEventListener`
     would not see it, and using jQuery here would break this file's no-library
     contract. Observing the node is also correct whichever code updated it:
     theme.js, an app, or a future refactor.

     theme.js formats via shop.money_format ("£{{amount}}"), which keeps trailing
     zeros ("£125.00"); the Liquid uses money_without_trailing_zeros ("£125").
     Strip a trailing .00 / ,00 so the two agree.

     Globo add-on charges are deliberately not reflected: they change what the
     customer pays, not the starting price this sentence is about. */

  function normalisePrice(text) {
    return text.replace(/([.,])00\s*$/, '').trim();
  }

  function bindPriceMirror(block) {
    var sourceId = block.getAttribute('data-ens-quote-ack-price-source');
    if (!sourceId) return;

    var source = document.getElementById(sourceId);
    var target = block.querySelector('[data-ens-quote-ack-price]');
    if (!source || !target || typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function () {
      var next = normalisePrice(source.textContent || '');
      if (next && next !== target.textContent) target.textContent = next;
    });

    observer.observe(source, { childList: true, characterData: true, subtree: true });
  }

  function initPriceMirrors() {
    var blocks = document.querySelectorAll(ACK_BLOCK);
    for (var i = 0; i < blocks.length; i++) bindPriceMirror(blocks[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPriceMirrors);
  } else {
    initPriceMirrors();
  }
})();
